import { type ReactNode, useState } from 'react'
import { toast } from 'react-hot-toast'

import { useAuth } from 'src/auth'
import {
  Button,
  Divider,
  Icon,
  IconButton,
  LogOutIcon as LogoutIcon,
  UserIcon as PersonIcon,
  Popover,
  SegmentedControl,
  Switch,
  Text,
} from 'src/design-system/ui'
import type { UiColorMode } from 'src/design-system/ui/themes'
import { navigate } from 'src/platform/router'

export const ProfessionalTopBar = ({
  leadingContent,
  actions,
  mode,
  chooseMode,
}: {
  leadingContent?: ReactNode
  actions?: ReactNode
  mode: UiColorMode
  chooseMode: (mode: UiColorMode) => void
}) => {
  const { currentUser, isAuthenticated, logOut } = useAuth()
  const [open, setOpen] = useState(false)
  const [businessUser, setBusinessUser] = useState(() => {
    try {
      return localStorage.getItem('isBusinessUser') === 'true'
    } catch {
      return false
    }
  })
  const roles = Array.isArray(currentUser?.roles)
    ? currentUser.roles.filter(
        (role): role is string => typeof role === 'string'
      )
    : []
  const logout = async () => {
    setOpen(false)
    try {
      await logOut({ postLogoutRedirectUri: window.location.origin })
      navigate('/')
    } catch {
      toast.error('Unable to logout.')
    }
  }
  return (
    <header className="professional-top-bar">
      {leadingContent}
      <img
        className="professional-wordmark-light"
        src="/Two Dots_Wordmark_Black.svg"
        alt="Two Dots"
      />
      <img
        className="professional-wordmark-dark"
        src="/Two Dots_Wordmark_White.svg"
        alt="Two Dots"
      />
      <div className="ds:ml-auto ds:flex ds:items-center ds:gap-2">
        {actions}
        {isAuthenticated && (
          <Popover
            open={open}
            onOpenChange={setOpen}
            ariaLabel="Account"
            placement="bottom end"
            className="professional-account-popover"
            trigger={
              <IconButton variant="quiet" aria-label="Account">
                <Icon icon={PersonIcon} className="professional-account-icon" />
              </IconButton>
            }
          >
            <div className="ds:grid ds:min-w-0 ds:gap-1 ds:p-2">
              <Text variant="body-strong">
                {typeof currentUser?.email === 'string'
                  ? currentUser.email
                  : 'Account'}
              </Text>
              {roles.length > 0 && (
                <Text variant="caption" tone="muted">
                  Roles: {roles.join(', ')}
                </Text>
              )}
            </div>
            {roles.includes('Super') && (
              <Switch
                isSelected={businessUser}
                onChange={(value) => {
                  setBusinessUser(value)
                  try {
                    if (value) localStorage.setItem('isBusinessUser', 'true')
                    else localStorage.removeItem('isBusinessUser')
                  } catch {
                    /* Local selection remains usable. */
                  }
                }}
              >
                Business user view
              </Switch>
            )}
            {roles.some((role) => ['Admin', 'Accountant'].includes(role)) && (
              <Button
                variant="quiet"
                size="compact"
                className="professional-account-action"
                onPress={() => {
                  setOpen(false)
                  navigate('/nonqm-accounting')
                }}
              >
                Mortgage accounting
              </Button>
            )}
            <Divider spacing="none" />
            <div className="ds:grid ds:gap-2 ds:p-2">
              <Text variant="caption-strong">Appearance</Text>
              <SegmentedControl
                label="Appearance"
                selectedKey={mode}
                items={[
                  { id: 'system', label: 'Auto' },
                  { id: 'light', label: 'Light' },
                  { id: 'dark', label: 'Dark' },
                ]}
                onSelectionChange={(key) => {
                  if (key === 'system' || key === 'light' || key === 'dark')
                    chooseMode(key)
                }}
              />
            </div>
            <Divider spacing="none" />
            <Button
              variant="quiet"
              size="compact"
              className="professional-account-action"
              onPress={() => void logout()}
            >
              <Icon icon={LogoutIcon} size="sm" />
              Logout
            </Button>
          </Popover>
        )}
      </div>
    </header>
  )
}
